import {LocalDate} from '@js-joda/core';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import InsertBelowIcon from '@material-ui/icons/ExpandLess';
import React, {useCallback, useMemo, useState} from 'react';
import {TimeEntry} from '../../pages/TimeEntries';
import TimeChip from './TimeChip';
import TimeEntryFormDialog from './TimeEntryFormDialog';
import {getTotalTime} from './TimeEntryTable';

const useStyles = makeStyles(theme => ({
    root: {
        backgroundColor: theme.palette.primary.dark,
        '& td': {
            color: theme.palette.primary.contrastText,
        },
        '& button': {
            color: theme.palette.primary.contrastText,
        },
    },
    billableHoursCell: {
        paddingRight: 0,
    },
}));

type Props = {
    date : LocalDate;
    timeEntries : TimeEntry[];
    onInsert : (timeEntry : TimeEntry) => Promise<void>;
};

const TimeEntryDateFooter : React.FC<Props> = ({date, timeEntries, onInsert} : Props) => {
    const classes = useStyles();
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const totalTime = getTotalTime(timeEntries);

    const initialValues : Partial<TimeEntry> | undefined = useMemo(() => {
        let latestUsableTime;

        for (const timeEntry of timeEntries) {
            if (!timeEntry.startTime || !timeEntry.minutes) {
                continue;
            }

            const endDateTime = timeEntry.startDate.atTime(timeEntry.startTime).plusMinutes(timeEntry.minutes);

            if (endDateTime.toLocalDate() > date) {
                return;
            }

            const endTime = endDateTime.toLocalTime();

            if (!latestUsableTime || latestUsableTime < endTime) {
                latestUsableTime = endTime;
            }
        }

        if (!latestUsableTime) {
            return;
        }

        return {
            startDate: date,
            startTime: latestUsableTime,
        };
    }, [date, timeEntries]);

    const handleSubmit = useCallback(async (timeEntry) => {
        setFormDialogOpen(false);
        return onInsert(timeEntry);
    }, [setFormDialogOpen, onInsert]);

    return (
        <TableRow className={classes.root}>
            <TableCell/>
            <Hidden smDown>
                <TableCell/>
                <TableCell/>
                <TableCell/>
            </Hidden>
            <TableCell className={classes.billableHoursCell}>
                <TimeChip minutes={totalTime.billable} billable={true}/>
            </TableCell>
            <TableCell>
                <TimeChip minutes={totalTime.total} billable={false}/>
            </TableCell>
            <TableCell>
                {initialValues && (
                    <>
                        <Tooltip title="Insert below">
                            <IconButton onClick={() => setFormDialogOpen(true)} size="small">
                                <InsertBelowIcon/>
                            </IconButton>
                        </Tooltip>
                        <TimeEntryFormDialog
                            timeEntry={initialValues}
                            open={formDialogOpen}
                            onClose={() => setFormDialogOpen(false)}
                            onSubmit={handleSubmit}
                        />
                    </>
                )}
            </TableCell>
        </TableRow>
    );
};

export default TimeEntryDateFooter;
